import { useCallback, useEffect, useRef, useState } from 'react'
import { isChromeExtensionOrigin } from '../routing/chromeCompanionProtocol'
import { PAGE_CONTEXT_REQUEST, parseChromePageContext } from '../routing/chromePageContext'

export default function useChromePageContextBridge(enabled) {
  const [available, setAvailable] = useState(false)
  const [snapshot, setSnapshot] = useState(null)
  const [revision, setRevision] = useState(0)
  const origin = useRef(null)
  const pending = useRef(null)
  const clear = useCallback(() => { pending.current = null; setSnapshot(null) }, [])
  useEffect(() => {
    if (!enabled) return undefined
    const receive = (event) => {
      if (event.source !== window.parent || !isChromeExtensionOrigin(event.origin)) return
      if (origin.current && origin.current !== event.origin) return
      const context = parseChromePageContext(event.data)
      if (!context) return
      origin.current = event.origin
      setAvailable(true)
      if (context.status === 'available') {
        clear()
        setRevision((value) => value + 1)
        return
      }
      if (context.requestId === pending.current) {
        pending.current = null
        setSnapshot(context.status === 'page' ? context : null)
      }
    }
    window.addEventListener('message', receive)
    return () => {
      window.removeEventListener('message', receive)
      origin.current = null
      clear()
      setAvailable(false)
    }
  }, [enabled, clear])
  const request = useCallback(() => {
    if (!enabled || !origin.current) return
    const requestId = crypto.randomUUID()
    pending.current = requestId
    setSnapshot(null)
    window.parent.postMessage({ type: PAGE_CONTEXT_REQUEST, version: 1, requestId }, origin.current)
  }, [enabled])
  return { available: enabled && available, revision, snapshot: enabled ? snapshot : null, request, clear }
}
